import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../api';
import logger from './../utils/logger.js';

const LushaCreditDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [creditUsage, setCreditUsage] = useState([]);
  const [enrichedContacts, setEnrichedContacts] = useState([]);
  const [summary, setSummary] = useState({});
  const [perUserStats, setPerUserStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedUser, setSelectedUser] = useState('');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadCreditUsage(),
        loadEnrichedContacts()
      ]);
    } catch (err) {
      logger.error('Error loading Lusha data:', err);
      setError('Failed to load Lusha data');
    } finally {
      setLoading(false);
    }
  };

  const loadCreditUsage = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedUser) params.append('user_id', selectedUser);
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      
      const response = await api.get(`/api/lusha/credit-usage?${params.toString()}`);
      
      if (response.data.success) {
        setCreditUsage(response.data.data);
        setSummary(response.data.summary);
        setPerUserStats(response.data.per_user_stats || []);
      } else {
        throw new Error(response.data.error || 'Failed to load credit usage');
      }
    } catch (err) {
      logger.error('Error loading credit usage:', err);
      throw err;
    }
  };

  const loadEnrichedContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedUser) params.append('user_id', selectedUser);
      
      const response = await api.get(`/api/lusha/enriched-contacts?${params.toString()}`);
      
      if (response.data.success) {
        setEnrichedContacts(response.data.data);
      } else {
        throw new Error(response.data.error || 'Failed to load enriched contacts');
      }
    } catch (err) {
      logger.error('Error loading enriched contacts:', err);
      throw err;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFilterChange = () => {
    loadData();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = (data, filename) => {
    const csvContent = [
      Object.keys(data[0]).join(','),
      ...data.map(row => Object.values(row).map(val => 
        typeof val === 'string' && val.includes(',') ? `"${val}"` : val
      ).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const SummaryCards = () => (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Credits Used
            </Typography>
            <Typography variant="h4" component="div">
              {summary.total_credits || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Email Credits
            </Typography>
            <Typography variant="h4" component="div">
              {summary.total_email_credits || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Phone Credits
            </Typography>
            <Typography variant="h4" component="div">
              {summary.total_phone_credits || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Enrichments
            </Typography>
            <Typography variant="h4" component="div">
              {summary.total_enrichments || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const CreditUsageTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Credits Used</TableCell>
            <TableCell>Email Credits</TableCell>
            <TableCell>Phone Credits</TableCell>
            <TableCell>Enriched Data</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {creditUsage.map((usage) => (
            <TableRow key={usage.id}>
              <TableCell>{formatDate(usage.enrichment_date)}</TableCell>
              <TableCell>{usage.user_display_name || usage.user_username}</TableCell>
              <TableCell>{usage.contact_id}</TableCell>
              <TableCell>
                <Chip 
                  label={usage.credits_used} 
                  color={usage.credits_used > 5 ? 'error' : 'primary'} 
                  size="small" 
                />
              </TableCell>
              <TableCell>{usage.email_credits}</TableCell>
              <TableCell>{usage.phone_credits}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {usage.enriched_email && (
                    <Tooltip title="Email enriched">
                      <EmailIcon color="primary" fontSize="small" />
                    </Tooltip>
                  )}
                  {usage.enriched_phone && (
                    <Tooltip title="Phone enriched">
                      <PhoneIcon color="primary" fontSize="small" />
                    </Tooltip>
                  )}
                  {usage.enriched_linkedin && (
                    <Tooltip title="LinkedIn enriched">
                      <LinkedInIcon color="primary" fontSize="small" />
                    </Tooltip>
                  )}
                  {usage.enriched_other && (
                    <Tooltip title="Other data enriched">
                      <PersonIcon color="primary" fontSize="small" />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const PerUserStatsTable = () => (
    <TableContainer component={Paper} sx={{ mt: 3 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Total Credits Used</TableCell>
            <TableCell>Total Enrichments</TableCell>
            <TableCell>Avg Credits per Enrichment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {perUserStats.map((stat) => (
            <TableRow key={stat.username}>
              <TableCell>{stat.display_name || stat.username}</TableCell>
              <TableCell>
                <Chip 
                  label={stat.total_credits} 
                  color={stat.total_credits > 50 ? 'error' : stat.total_credits > 20 ? 'warning' : 'primary'} 
                />
              </TableCell>
              <TableCell>{stat.total_enrichments}</TableCell>
              <TableCell>
                {stat.total_enrichments > 0 
                  ? (stat.total_credits / stat.total_enrichments).toFixed(1)
                  : '0'
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const EnrichedContactsTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Enriched Date</TableCell>
            <TableCell>User</TableCell>
            <TableCell>Contact Name</TableCell>
            <TableCell>Job Title</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>LinkedIn</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {enrichedContacts.map((contact) => (
            <TableRow key={contact.id}>
              <TableCell>{formatDate(contact.enriched_at)}</TableCell>
              <TableCell>{contact.user_display_name || contact.user_username}</TableCell>
              <TableCell>{contact.full_name}</TableCell>
              <TableCell>{contact.job_title}</TableCell>
              <TableCell>{contact.company_name}</TableCell>
              <TableCell>
                {contact.work_email && (
                  <Tooltip title={contact.work_email}>
                    <EmailIcon color="primary" fontSize="small" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                {(contact.mobile_phone || contact.direct_phone) && (
                  <Tooltip title={contact.mobile_phone || contact.direct_phone}>
                    <PhoneIcon color="primary" fontSize="small" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                {contact.linkedin_url && (
                  <Tooltip title={contact.linkedin_url}>
                    <LinkedInIcon color="primary" fontSize="small" />
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Lusha Credit Usage Dashboard
        </Typography>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>User</InputLabel>
                  <Select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    label="User"
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.display_name || user.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  onClick={handleFilterChange}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <SummaryCards />

            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
              <Tab label="Credit Usage" />
              <Tab label="Enriched Contacts" />
              <Tab label="User Statistics" />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Credit Usage History</Typography>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={() => exportToCSV(creditUsage, 'lusha-credit-usage.csv')}
                    disabled={creditUsage.length === 0}
                  >
                    Export CSV
                  </Button>
                </Box>
                <CreditUsageTable />
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Enriched Contacts</Typography>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={() => exportToCSV(enrichedContacts, 'lusha-enriched-contacts.csv')}
                    disabled={enrichedContacts.length === 0}
                  >
                    Export CSV
                  </Button>
                </Box>
                <EnrichedContactsTable />
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Per-User Statistics
                </Typography>
                <PerUserStatsTable />
              </Box>
            )}
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default LushaCreditDashboard; 